import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CustomerLite,
  OrderDto,
  OrderInput,
  OrderItemInput,
  OrderPaymentInput,
  OrderSummaryDto,
  PosBootstrap,
} from '../models/pos.models';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface PaymentResult {
  id: string;
  status: string;
  total: number;
  paid_total: number;
  change_due: number;
  balance_due: number;
  payments: OrderDto['payments'];
}

export interface SplitResult {
  parent: OrderDto;
  children: OrderDto[];
}

@Injectable({ providedIn: 'root' })
export class PosApiService {
  private readonly base = environment.apiUrl;

  constructor(private http: HttpClient) {}

  bootstrap(): Observable<ApiResponse<PosBootstrap>> {
    return this.http.get<ApiResponse<PosBootstrap>>(`${this.base}/pos/bootstrap`);
  }

  // Orders ------------------------------------------------------------------
  listOrders(params: { status?: string; date?: string } = {}): Observable<ApiResponse<OrderSummaryDto[]>> {
    // `all=true` bypasses server-side pagination so callers (e.g. merge-bills)
    // receive every matching order, not just the first page.
    let httpParams = new HttpParams().set('all', 'true');
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.date) httpParams = httpParams.set('date', params.date);
    return this.http.get<ApiResponse<OrderSummaryDto[]>>(`${this.base}/orders`, { params: httpParams });
  }

  held(): Observable<ApiResponse<OrderDto[]>> {
    return this.http.get<ApiResponse<OrderDto[]>>(`${this.base}/orders/held`);
  }

  getOrder(id: string): Observable<ApiResponse<OrderDto>> {
    return this.http.get<ApiResponse<OrderDto>>(`${this.base}/orders/${id}`);
  }

  createOrder(input: OrderInput): Observable<ApiResponse<OrderDto>> {
    return this.http.post<ApiResponse<OrderDto>>(`${this.base}/orders`, input);
  }

  updateOrder(id: string, input: OrderInput): Observable<ApiResponse<OrderDto>> {
    return this.http.put<ApiResponse<OrderDto>>(`${this.base}/orders/${id}`, input);
  }

  /** Append a round of items to an existing order (reservation running tabs). */
  addItems(id: string, items: OrderItemInput[]): Observable<ApiResponse<OrderDto>> {
    return this.http.post<ApiResponse<OrderDto>>(`${this.base}/orders/${id}/items`, { items });
  }

  holdOrder(id: string, holdLabel: string | null): Observable<ApiResponse<OrderDto>> {
    return this.http.patch<ApiResponse<OrderDto>>(`${this.base}/orders/${id}/hold`, { hold_label: holdLabel });
  }

  /** Reopen a held order to `open` so it can be settled (reservation tabs). */
  reopenOrder(id: string): Observable<ApiResponse<OrderDto>> {
    return this.http.patch<ApiResponse<OrderDto>>(`${this.base}/orders/${id}/reopen`, {});
  }

  voidOrder(id: string, reason: string | null): Observable<ApiResponse<OrderDto>> {
    return this.http.patch<ApiResponse<OrderDto>>(`${this.base}/orders/${id}/void`, { reason });
  }

  applyDiscount(
    id: string,
    payload: { discount_type: string | null; discount_value: number; discount_reason?: string | null },
  ): Observable<ApiResponse<OrderDto>> {
    return this.http.patch<ApiResponse<OrderDto>>(`${this.base}/orders/${id}/discount`, payload);
  }

  split(id: string, splits: Array<{ items: Array<{ order_item_id: string; quantity: number }> }>): Observable<ApiResponse<SplitResult>> {
    return this.http.post<ApiResponse<SplitResult>>(`${this.base}/orders/${id}/split`, { splits });
  }

  merge(orderIds: string[]): Observable<ApiResponse<OrderDto>> {
    return this.http.post<ApiResponse<OrderDto>>(`${this.base}/orders/merge`, { order_ids: orderIds });
  }

  addPayment(orderId: string, payment: OrderPaymentInput): Observable<ApiResponse<PaymentResult>> {
    return this.http.post<ApiResponse<PaymentResult>>(`${this.base}/orders/${orderId}/payments`, payment);
  }

  redeemPoints(orderId: string, points: number): Observable<ApiResponse<PaymentResult & { points_balance: number }>> {
    return this.http.post<ApiResponse<PaymentResult & { points_balance: number }>>(
      `${this.base}/orders/${orderId}/loyalty/redeem`,
      { points },
    );
  }

  // Customers ---------------------------------------------------------------
  searchCustomers(search: string): Observable<ApiResponse<CustomerLite[]>> {
    const params = new HttpParams().set('search', search);
    return this.http.get<ApiResponse<CustomerLite[]>>(`${this.base}/customers`, { params });
  }

  createCustomer(payload: Partial<CustomerLite>): Observable<ApiResponse<CustomerLite>> {
    return this.http.post<ApiResponse<CustomerLite>>(`${this.base}/customers`, payload);
  }
}
